import React, { useMemo, useState } from 'react';
import { ArrowUpDown, ExternalLink, Copy, Check, CheckCircle2, XCircle, HelpCircle } from 'lucide-react';
import { WalletData } from '../data/wallets';
import { formatBTC, formatUSD, formatPercent, shortenAddress, copyToClipboard, getBlockchainExplorerUrl } from '../utils/formatters';
import { Link } from 'react-router-dom';
interface WalletTableProps {
  wallets: WalletData[];
}
type SortField = 'rank' | 'balance' | 'percentOfSupply' | 'lastActivity';
type SortDirection = 'asc' | 'desc';
export function WalletTable({
  wallets
}: WalletTableProps) {
  const [sortField, setSortField] = useState<SortField>('rank');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const sortedAndFilteredWallets = useMemo(() => {
    let filtered = wallets;
    if (searchTerm) {
      filtered = wallets.filter(wallet => wallet.address.toLowerCase().includes(searchTerm.toLowerCase()) || wallet.label?.toLowerCase().includes(searchTerm.toLowerCase()) || wallet.entity?.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    return [...filtered].sort((a, b) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];
      if (sortField === 'lastActivity') {
        aVal = aVal === 'Never' ? 0 : new Date(aVal).getTime();
        bVal = bVal === 'Never' ? 0 : new Date(bVal).getTime();
      }
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  }, [wallets, sortField, sortDirection, searchTerm]);
  const handleCopy = async (address: string) => {
    await copyToClipboard(address);
    setCopiedAddress(address);
    setTimeout(() => setCopiedAddress(null), 2000);
  };
  const getCategoryBadge = (category: WalletData['category']) => {
    const styles = {
      exchange: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
      whale: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
      institutional: 'bg-green-500/10 text-green-500 border-green-500/20',
      mining: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
      lost: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
      unknown: 'bg-gray-500/10 text-gray-400 border-gray-500/20'
    };
    return <span className={`px-2 py-1 rounded text-xs font-medium border ${styles[category]}`} data-id="element-76">
        {category.charAt(0).toUpperCase() + category.slice(1)}
      </span>;
  };
  const getVerificationBadge = (verified: boolean | null) => {
    if (verified === true) {
      return <CheckCircle2 className="w-4 h-4 text-emerald-400" title="Verified owner" data-id="element-77" />;
    }
    if (verified === false) {
      return <XCircle className="w-4 h-4 text-red-400" title="Disputed ownership" data-id="element-78" />;
    }
    return <HelpCircle className="w-4 h-4 text-gray-500" title="Uncertain ownership" data-id="element-79" />;
  };
  return <div className="space-y-4" data-id="element-80">
      {/* Search */}
      <div className="flex items-center gap-4" data-id="element-81">
        <input type="text" placeholder="Search by address, label, or entity..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors" data-id="element-82" />
        <div className="text-gray-400 text-sm" data-id="element-83">
          {sortedAndFilteredWallets.length} wallets
        </div>
      </div>

      {/* Table */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-xl overflow-hidden" data-id="element-84">
        <div className="overflow-x-auto" data-id="element-85">
          <table className="w-full" data-id="element-86">
            <thead className="bg-gray-900/50 border-b border-gray-700" data-id="element-87">
              <tr data-id="element-88">
                <th className="px-6 py-4 text-left" data-id="element-89">
                  <button onClick={() => handleSort('rank')} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors font-medium text-sm" data-id="element-90">
                    Rank
                    <ArrowUpDown className="w-4 h-4" data-id="element-91" />
                  </button>
                </th>
                <th className="px-6 py-4 text-left" data-id="element-92">
                  <span className="text-gray-400 font-medium text-sm" data-id="element-93">
                    Address
                  </span>
                </th>
                <th className="px-6 py-4 text-left" data-id="element-94">
                  <span className="text-gray-400 font-medium text-sm" data-id="element-95">
                    Owner / Label
                  </span>
                </th>
                <th className="px-6 py-4 text-right" data-id="element-96">
                  <button onClick={() => handleSort('balance')} className="flex items-center gap-2 ml-auto text-gray-400 hover:text-white transition-colors font-medium text-sm" data-id="element-97">
                    Balance (BTC)
                    <ArrowUpDown className="w-4 h-4" data-id="element-98" />
                  </button>
                </th>
                <th className="px-6 py-4 text-right" data-id="element-99">
                  <span className="text-gray-400 font-medium text-sm" data-id="element-100">
                    Balance (USD)
                  </span>
                </th>
                <th className="px-6 py-4 text-right" data-id="element-101">
                  <button onClick={() => handleSort('percentOfSupply')} className="flex items-center gap-2 ml-auto text-gray-400 hover:text-white transition-colors font-medium text-sm" data-id="element-102">
                    % Supply
                    <ArrowUpDown className="w-4 h-4" data-id="element-103" />
                  </button>
                </th>
                <th className="px-6 py-4 text-center" data-id="element-104">
                  <span className="text-gray-400 font-medium text-sm" data-id="element-105">
                    Links
                  </span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700" data-id="element-106">
              {sortedAndFilteredWallets.map(wallet => <tr key={wallet.address} className="hover:bg-gray-800/50 transition-colors" data-id="element-107">
                  <td className="px-6 py-4" data-id="element-108">
                    <span className="text-white font-bold font-mono" data-id="element-109">
                      #{wallet.rank}
                    </span>
                  </td>
                  <td className="px-6 py-4" data-id="element-110">
                    <div className="flex items-center gap-2" data-id="element-111">
                      <code className="text-orange-500 font-mono text-sm" data-id="element-112">
                        {shortenAddress(wallet.address)}
                      </code>
                      <button onClick={() => handleCopy(wallet.address)} className="text-gray-400 hover:text-white transition-colors" data-id="element-113">
                        {copiedAddress === wallet.address ? <Check className="w-4 h-4 text-green-500" data-id="element-114" /> : <Copy className="w-4 h-4" data-id="element-115" />}
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-116">
                    <div className="flex items-center gap-2" data-id="element-117">
                      {getVerificationBadge(wallet.verified)}
                      <div className="flex flex-col gap-1" data-id="element-118">
                        {wallet.label && <span className="text-white text-sm font-medium" data-id="element-119">
                            {wallet.label}
                          </span>}
                        {wallet.entity && <span className="text-gray-400 text-xs" data-id="element-120">
                            {wallet.entity}
                          </span>}
                        {getCategoryBadge(wallet.category)}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-121">
                    <span className="text-white font-mono font-medium" data-id="element-122">
                      {formatBTC(wallet.balance)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-123">
                    <span className="text-gray-300 font-mono" data-id="element-124">
                      {formatUSD(wallet.balanceUSD)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-125">
                    <span className="text-orange-500 font-mono font-medium" data-id="element-126">
                      {formatPercent(wallet.percentOfSupply)}
                    </span>
                  </td>
                  <td className="px-6 py-4" data-id="element-127">
                    <div className="flex items-center justify-center gap-2" data-id="element-128">
                      <a href={getBlockchainExplorerUrl(wallet.address, 'blockchain')} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-orange-500 transition-colors" title="View on Blockchain.com" data-id="element-129">
                        <ExternalLink className="w-4 h-4" data-id="element-130" />
                      </a>
                      <Link to={`/wallet/${wallet.address}`} className="text-gray-400 hover:text-orange-500 transition-colors text-sm font-medium" data-id="element-131">
                        Details
                      </Link>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}